import { useState, useEffect, createContext, useContext } from "react";
// Update imports to only include the new password-based loginUser
import { logoutUserApi, checkAuthStatusApi, loginUser } from "../api/getApi";

// Create context
const AuthContext = createContext();

// Custom hook
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Function to check auth status - remains unchanged and correct
  const checkAuthStatus = async () => {
    try {
      const data = await checkAuthStatusApi();
      setIsAuthenticated(data.isAuthenticated);
      setUser(data.user || null);

      if (data.isAuthenticated && data.user) {
        sessionStorage.setItem("userName", data.user.name);
        sessionStorage.setItem("userMobile", data.user.mobile);
      } else {
        sessionStorage.removeItem("userName");
        sessionStorage.removeItem("userMobile");
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Logout function - remains unchanged and correct
  const logout = async () => {
    try {
      await logoutUserApi();
      sessionStorage.removeItem("userName");
      sessionStorage.removeItem("userMobile");
      sessionStorage.removeItem("selectedColleges");
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // NEW: Login function for password-based authentication
  const login = async (identifier, password) => {
    try {
      const response = await loginUser(identifier, password);
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        // Store user info for UI persistence within the session
        if (response.user) {
          sessionStorage.setItem("userName", response.user.name);
          sessionStorage.setItem("userMobile", response.user.mobile);
        }
        return { success: true };
      } else {
        // This 'else' block is for non-axios errors where success is false
        return { success: false, error: response.error || "Login failed" };
      }
    } catch (error) {
      // This 'catch' block handles axios errors (e.g., 401, 500)
      const errorData = error.response?.data;
      return {
        success: false,
        // Pass the specific error message from the backend
        error: errorData?.error || "An unknown error occurred during login.",
      };
    }
  };

  // Check auth status on initial mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Updated context value with the new login function
  const value = {
    isAuthenticated,
    user,
    loading,
    checkAuthStatus,
    logout,
    login, // The new password-based login function
    setUser,
    setIsAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
